import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import notifyPopup from 'components/Common/Notification/notifyPopup'
import MuiPhoneForm from 'components/Common/Phone/muiPhoneForm'
import SectionHeader from 'components/Common/SectionHeader'
import Form from 'components/RouteWizard/Form'
import Input from 'components/RouteWizard/Input'
import { Select } from 'components/RouteWizard/Select'
import useUserRole from 'hooks/useUserRole'
import React, { useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { registerUser } from 'reducers/usersReducer'
import { newUserSchema } from './schema'

interface FormData {
  firstName: string
  lastName: string
  emailAddress?: string
  mobileNumber?: string
  role: string
}

interface InviteUserProps {
  closeDrawer: () => void
  client: any
  setDirtyDrawer: (dirty: boolean) => void
}

enum travelPolicySpendAttributes {
  spendLimitPerJob = 'Spend Limit Per Job',
  spendLimitPerDay = 'Spend Limit Per Day',
  spendLimitPerWeek = 'Spend Limit Per Week',
  spendLimitPerMonth = 'Spend Limit Per Month',
  groupSpendLimitPerDay = 'Client Spend Limit Per Day',
  groupSpendLimitPerWeek = 'Client Spend Limit Per Week',
  groupSpendLimitPerMonth = 'Client Spend Limit Per Month',
}
const InviteUser = (props: InviteUserProps) => {
  const formMethods = useRef<UseFormReturn | null>(null)
  const [submitPressed, setSubmitPressed] = React.useState(false)
  const dispatch = useDispatch()

  React.useEffect(() => {
    if (!formMethods.current) return
    const subscription = formMethods.current.watch(() => {
      props.setDirtyDrawer(true)
    })
    return () => subscription?.unsubscribe?.()
  }, [props])

  const onSubmit = async (formData: any) => {
    const changedObject: Partial<FormData> = Object.keys(formData).reduce(
      (result, key) => {
        if (formMethods.current?.formState.dirtyFields[key]) {
          result[key] = formData[key]
        }
        return result
      },
      {},
    )
    console.log('changedObject: ', changedObject)
    if (changedObject.mobileNumber && changedObject.mobileNumber.length < 6) {
      delete changedObject.mobileNumber
    } else if (changedObject.mobileNumber) {
      changedObject.contact = { mobileNumber: changedObject.mobileNumber }
    }

    const submitData = {
      ...changedObject,
      roles: [changedObject.role],
      clientId: props.client.id,
    }

    setSubmitPressed(true)
    try {
      await dispatch(registerUser(submitData))
        .unwrap()
        .then((then: any) => {
          notifyPopup({
            type: 'success',
            title: 'Invite Sent',
            message: `Your invite has been sent to ${then.data.email}`,
          })
          props.closeDrawer()
        })
    } catch (error: any) {
      setSubmitPressed(false)
      notifyPopup({
        type: 'danger',
        title: 'Error',
        message: error.error.message,
      })
    }
  }

  const formDefaults = {
    firstName: '',
    lastName: '',
    emailAddress: '',
    mobileNumber: '',
  }

  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        fontSize: '14px',
        backgroundColor: '#F8F8F8',
        color: '#8E8E8E',
        display: 'flex',
        flexShrink: 1,
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: '20px',
      }}
    >
      <Grid
        container
        style={{
          width: '90%',
          display: 'block',
          textAlign: 'center',
          paddingTop: '20px',
        }}
      >
        <Form
          schema={newUserSchema}
          ref={formMethods}
          onSubmit={onSubmit}
          submitText="Create Account"
          defaultValues={formDefaults}
          isLoading={submitPressed}
        >
          <>
            <SectionHeader title="Create New Account" />
            <Input name="firstName" label="First Name" />
            <Input name="lastName" label="Last Name" />
            <Input name="email" label="Email Address" />
            <MuiPhoneForm name="mobileNumber" label="Mobile Number" fullWidth />
            <Select
              name="role"
              label="Role"
              options={useUserRole().filteredRoles.reduce((acc, role) => {
                acc[role.role] = role.display
                return acc
              }, {} as { [key: string]: string | number })}
              sx={{ flexGrow: 1, textAlign: 'left' }}
            />
            <Box sx={{ marginTop: '20px', textAlign: 'left' }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Available Roles</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {useUserRole().filteredRoles.map((role) => (
                    <Grid
                      container
                      key={role.role}
                      sx={{ marginBottom: '10px' }}
                    >
                      <Grid item xs={3}>
                        <strong>{role.display}</strong>:
                      </Grid>
                      <Grid item xs={9}>
                        {role.description}
                      </Grid>
                    </Grid>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Box>

            <Select
              name="travelPolicyId"
              label="Travel Policy"
              defaultValue={null}
              options={Object.fromEntries(
                props.client.travelPolicies.map(
                  ({ id, name }: { id: number; name: string }) => [id, name],
                ),
              )}
              sx={{ flexGrow: 1, textAlign: 'left' }}
            />
            <Box sx={{ marginTop: '20px', textAlign: 'left' }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Travel Policies</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableCell>Name</TableCell>
                        <TableCell>Policy Rule</TableCell>
                      </TableHead>
                      <TableBody>
                        {props.client.travelPolicies.map((policy: any) => (
                          <TableRow key={policy.id}>
                            <TableCell>{policy.name}</TableCell>
                            <TableCell>
                              <Accordion>
                                <AccordionSummary>
                                  Permitted Vehicles
                                </AccordionSummary>
                                <AccordionDetails>
                                  {policy.taxi.authorizedVehicles.map(
                                    (v: string) => (
                                      <Box key={v}>
                                        {String(v).replaceAll('_', ' ')}{' '}
                                      </Box>
                                    ),
                                  )}
                                </AccordionDetails>
                              </Accordion>
                              <Accordion>
                                <AccordionSummary>
                                  Permitted Booking Times
                                </AccordionSummary>
                                <AccordionDetails>
                                  {Object.entries(policy.taxi.validJourneyTimes)
                                    .length > 0
                                    ? Object.entries(
                                        policy.taxi.validJourneyTimes,
                                      ).map(([key, value]: [string, any]) => (
                                        <Box key={key}>{value} </Box>
                                      ))
                                    : 'Any time'}
                                </AccordionDetails>
                              </Accordion>
                              <Accordion>
                                <AccordionSummary>
                                  Spend Limits
                                </AccordionSummary>
                                <AccordionDetails>
                                  {Object.entries(policy.taxi).map(
                                    (k: any, idx: number) => {
                                      if (
                                        Object.keys(
                                          travelPolicySpendAttributes,
                                        ).includes(k[0])
                                      )
                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                            }}
                                          >
                                            <Box>
                                              {Object.entries(
                                                travelPolicySpendAttributes,
                                              ).find(
                                                (t: any) => t[0] === k[0],
                                              )?.[1] ?? ''}
                                            </Box>
                                            <Box>{`${k[1]}`}</Box>
                                          </Box>
                                        )
                                    },
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          </>
        </Form>
      </Grid>
    </Box>
  )
}

export default InviteUser
